import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { type ZodEnum } from 'zod';
import { AuthState, useIdentify } from '@hungryroot/rest-hooks';
import { PRECHECKOUT_PLAN_ROUTE } from '@hungryroot/routes';
import { QUIZ_NAVIGATION_LOCAL_STORAGE_KEY } from '../defs/localStorage';
import { postSignUpRoutes } from '../navigation/funnel-routes';
import {
  createSerializedNativationSchema,
  deserializeNavState,
  getNavResumeRoute,
} from '../navigation/schema';
import { useRemoteQuizRequiredQuestions } from './useRemoteQuiz';

type Props<R extends [string, ...string[]]> = {
  routesSchema: ZodEnum<R>;
  defaultRoute?: R[number];
};

export function useNavigationResumeUrl<R extends [string, ...string[]]>({
  routesSchema,
  defaultRoute,
}: Props<R>) {
  const [localStorageValue] = useLocalStorage(
    QUIZ_NAVIGATION_LOCAL_STORAGE_KEY
  );
  const [isMounted, setIsMounted] = useState(false);

  const customer = useIdentify();
  const isRequiredQuizComplete = useRemoteQuizRequiredQuestions({ customer });

  // Handle SRR'ing this
  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (isMounted === false) {
    return undefined;
  }

  if (defaultRoute === undefined) {
    return undefined;
  }

  const deserializer = createSerializedNativationSchema(routesSchema);
  const deserialized = deserializeNavState(localStorageValue, deserializer);

  if (deserialized === undefined) {
    if (isRequiredQuizComplete !== undefined) {
      return PRECHECKOUT_PLAN_ROUTE;
    }
    return undefined;
  }

  const resumeRoute = getNavResumeRoute({
    defaultRoute,
    navigationState: deserialized,
  });

  if (
    customer.authState === AuthState.isAnonymous &&
    resumeRoute !== undefined &&
    // The types in this function are probably overly strict
    // escape hatching this to string[] for the time being
    (postSignUpRoutes as readonly string[]).includes(resumeRoute)
  ) {
    return `/login/?next=${resumeRoute}` as const;
  }

  return resumeRoute;
}
